import {
  AssignV3ActorAssigneeType,
  AssignV3Strategy,
} from "@redotech/redo-flows-common/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Replacement for the legacy `support.autoAssignOnResponse` team setting and
 * its two reassignment siblings (`autoReassignFromAiOnResponse`,
 * `autoReassignFromHumanOnResponse`). When a merchant user posts a public
 * message, assign the conversation to them — but only if the conversation's
 * *current* assignment state is one the merchant has opted into.
 *
 * The rule's `enabled` flag mirrors `autoAssignOnResponse`. The two reassign
 * settings widen `assignmentTypes` from the baseline `[NO_ASSIGNEE]` to also
 * include `AI_AGENT_ASSIGNEE` and/or `SUPPORT_AGENT_ASSIGNEE`. Translation
 * lives in `responseAssignmentRulesToConfig`
 * (`support-settings-mapping.ts`); merchants edit three booleans, never the
 * raw array.
 *
 * `assigneeTypes` defaults to `["USER"]` — only human-merchant responses
 * trigger (re)assignment by default. Merchants can include `"AI"` in the
 * allowlist if they want AI replies to (re)claim conversations too.
 */
export const AssignOnMerchantResponseConfigSchema = Schema.Struct({
  /** Trigger-actor types eligible to receive the assignment. */
  assigneeTypes: Schema.Array(Schema.Literal("USER", "AI")),
  /**
   * Allowed *current* assignee states. Bound into the single
   * `ASSIGNMENT IS …` condition clause: empty → rule never matches, full
   * → matches all three states.
   */
  assignmentTypes: Schema.Array(
    Schema.Literal(
      AssignmentTypeOption.NO_ASSIGNEE,
      AssignmentTypeOption.AI_AGENT_ASSIGNEE,
      AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
    ),
  ),
});

export type AssignOnMerchantResponseConfig =
  typeof AssignOnMerchantResponseConfigSchema.Type;

const bind = makeBind<AssignOnMerchantResponseConfig>();

const DEFAULT_ASSIGNEE_TYPES: readonly AssignV3ActorAssigneeType[] = ["USER"];

// Defaults mirror the legacy Support V1 fallbacks:
// autoAssignOnResponse=true → enabled, NO_ASSIGNEE always present;
// autoReassignFromAiOnResponse=true → AI_AGENT_ASSIGNEE included;
// autoReassignFromHumanOnResponse=false → SUPPORT_AGENT_ASSIGNEE excluded.
const DEFAULT_ASSIGNMENT_TYPES: readonly AssignmentTypeOption[] = [
  AssignmentTypeOption.NO_ASSIGNEE,
  AssignmentTypeOption.AI_AGENT_ASSIGNEE,
];

export const assignOnMerchantResponseRule = {
  id: "default-assign-on-merchant-response",
  name: "Assign on merchant response",
  description:
    "When a merchant user responds, assign the conversation to them.",
  hidden: false,
  triggerType: RuleTriggerType.MERCHANT_MESSAGE_CREATED,

  configSchema: AssignOnMerchantResponseConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-assign-on-merchant-response",
    enabled: true,
    sortIndex: 6,
    configurations: {
      assigneeTypes: DEFAULT_ASSIGNEE_TYPES,
      assignmentTypes: DEFAULT_ASSIGNMENT_TYPES,
    },
  },

  template: {
    name: "Assign on merchant response",
    description:
      "When a merchant user responds, assign the conversation to them.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.MERCHANT_MESSAGE_CREATED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: bind(
                  "assignmentTypes",
                  DEFAULT_ASSIGNMENT_TYPES,
                ),
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.MESSAGE_TYPE,
              messageTypes: ["internalNote"],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: {
              _tag: "actor",
              assigneeTypes: bind("assigneeTypes", DEFAULT_ASSIGNEE_TYPES),
            },
            // `strategy` and `maxTicketsPerUser` are required by the action
            // schema but ignored by the `actor` branch — set to defaults so
            // the seeded flow always validates.
            strategy: AssignV3Strategy.ROUND_ROBIN,
            maxTicketsPerUser: null,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<string, AssignOnMerchantResponseConfig>;
